import styles from './ProblematicPage.module.scss';
import cn from 'classnames';
import CustomsTab from './tabs/CustomsTab.tsx';
import DisposalTab from './tabs/DisposalTab.tsx';
import FilterIcon from '@assets/icons/filter.svg';
import FirstMileTab from './tabs/FirstMileTab.tsx';
import OnWayTab from './tabs/OnWayTab.tsx';
import Page from '@components/Page';
import useTab from '@hooks/useTab';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import { TabsContent, TabsList } from '@components/Tabs/Tabs';
import {
  getReexportTariffsData,
  useProblematicCustomsCounter,
  useProblematicDisposalCounter,
  useProblematicFirstMileCounter,
  useProblematicOnWayCounter
} from '@api/apiClient/problematic';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

const ProblematicPage: React.FC = (): React.ReactElement => {
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const { sellersNotFound } = useSellersCounter();
  const { t } = useTranslation();

  const { data: customsTotal, refetch: customsRefetch } = useProblematicCustomsCounter(!sellersNotFound);
  const { data: disposalTotal, refetch: disposalRefetch } = useProblematicDisposalCounter(!sellersNotFound);
  const { data: firstMileTotal, refetch: firstMileRefetch } = useProblematicFirstMileCounter(!sellersNotFound);
  const { data: onWayTotal, refetch: onWayRefetch } = useProblematicOnWayCounter(!sellersNotFound);

  const {
    data: dataReexportTariffsData
  } = useQuery({
    queryKey: ['reexportTariffsData'],
    queryFn: () => getReexportTariffsData(),
    retry: 1,
    enabled: !sellersNotFound
  });

  const tabs = [
    {
      id: 'FirstMileTab',
      title: t('problematicParcelsPage.firstMile.title'),
      content: (
          <FirstMileTab
              isFilterOpen={isFilterOpen}
              onUpdateCustoms={customsRefetch}
              onUpdateDisposal={disposalRefetch}
              onUpdateFirstMile={firstMileRefetch}
              onUpdateOnWay={onWayRefetch}
              sellersNotFound={sellersNotFound}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: firstMileTotal
    },
    {
      id: 'CustomsTab',
      title: t('problematicParcelsPage.customs.title'),
      content: (
          <CustomsTab
              isFilterOpen={isFilterOpen}
              onUpdateCustoms={customsRefetch}
              onUpdateDisposal={disposalRefetch}
              onUpdateFirstMile={firstMileRefetch}
              onUpdateOnWay={onWayRefetch}
              reexportTariffsData={dataReexportTariffsData!}
              sellersNotFound={sellersNotFound}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: customsTotal
    },
    {
      id: 'OnWayTab',
      title: t('problematicParcelsPage.tabReturns'),
      content: (
          <OnWayTab
              isFilterOpen={isFilterOpen}
              reexportTariffsData={dataReexportTariffsData!}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: onWayTotal,
      disable: sellersNotFound
    },
    {
      id: 'DisposalTab',
      title: t('problematicParcelsPage.tabDisposal'),
      content: (
          <DisposalTab
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
          />
      ),
      count: disposalTotal,
      disable: sellersNotFound
    }
  ];

  const { activeTab, setActiveTab } = useTab(tabs);

  return (
      <Page title={t('problematicParcelsPage.title')}>
        <div className={styles.tabsRow}>
          <TabsList
              onChange={setActiveTab}
              tabs={tabs}
              value={activeTab}
          />
          {!sellersNotFound && (
              <div className={styles.filters}>
                <Button
                    className={cn(isFilterOpen ? styles.filterBtnActive : styles.filterBtnInactive)}
                    icon={FilterIcon}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    size={ButtonSize.S}
                    type={ButtonType.ROUNDED}
                    title={t('filters.filters')}
                />
              </div>
          )}
        </div>
        <TabsContent tab={tabs.find((tab) => (tab.id === activeTab))}/>
      </Page>
  );
};

export default ProblematicPage;